import React, { useState, useEffect, useContext } from 'react';
import {
  ShieldCheckIcon,
  EyeIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowDownTrayIcon,
  UserIcon,
  XCircleIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  DocumentArrowDownIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../utils/apiClient';
import { AuthContext } from '../App';
import ConfirmationModal from '../components/ConfirmationModal';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

interface PrivacyRequest {
  reference_number: string;
  request_type: 'data-access' | 'delete-account';
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  created_at: string;
  member_email?: string;
  notes?: string;
}

interface Statistics {
  total_count: number;
  pending_count: number;
  processing_count: number;
  completed_count: number;
  data_access_count: number;
  deletion_count: number;
}

interface MemberData {
  personal_data?: any;
  dogs?: any[];
  calendar_bookings?: any[];
  calendar_events_as_trainer?: any[];
  drinks?: any[];
  guest_cards?: any[];
  guest_card_bookings?: any[];
  roles?: any[];
  documents?: any[];
  finance_bookings?: any[];
  finance_details?: any[];
  paypal_payments?: any[];
  notifications?: any;
  planning?: any[];
  news?: any[];
  member_audits?: any[];
  audit_logs?: any[];
  auth_logs?: any[];
  privacy_consents?: any[];
}

const PrivacyManagement: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _auth = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState<'requests' | 'statistics' | 'data-export'>('requests');
  const [requests, setRequests] = useState<PrivacyRequest[]>([]);
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<PrivacyRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [memberDataEmail, setMemberDataEmail] = useState('');
  const [memberData, setMemberData] = useState<MemberData | null>(null);

  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'warning' | 'error' | 'question' | 'info';
    title: string;
    message: string;
    variant?: 'single' | 'two';
    onConfirm?: () => void;
  }>({ isOpen: false, type: 'info', title: '', message: '', variant: 'single' });

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';
  const subTileClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const inputClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';
  const primaryBtn = 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold rounded-xl transition disabled:opacity-50 disabled:cursor-not-allowed';

  const loadRequests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/privacy/requests');
      setRequests(response.data.requests || []);
    } catch (err: any) {
      // 404 = Endpoint noch nicht implementiert → still Empty-State zeigen, kein Fatal-Modal
      if (err.response?.status === 404) {
        setRequests([]);
      } else {
        setConfirmModal({
          isOpen: true, type: 'error', title: 'Fehler',
          message: err.response?.data?.message || 'Fehler beim Laden der Anfragen', variant: 'single'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/privacy/statistics');
      setStatistics(response.data.statistics);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setStatistics(null);
      } else {
        setConfirmModal({
          isOpen: true, type: 'error', title: 'Fehler',
          message: err.response?.data?.message || 'Fehler beim Laden der Statistiken', variant: 'single'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRequestDetails = async (reference: string) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/api/privacy/requests/${reference}`);
      setSelectedRequest(response.data.request);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true, type: 'error', title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Details', variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateRequestStatus = async (reference: string, status: string, notes: string) => {
    setLoading(true);
    try {
      await apiClient.put(`/api/privacy/requests/${reference}/status`, { status, notes });
      setConfirmModal({
        isOpen: true, type: 'success', title: 'Erfolgreich',
        message: 'Status wurde erfolgreich aktualisiert', variant: 'single'
      });
      loadRequests();
      if (selectedRequest) loadRequestDetails(reference);
    } catch (err: any) {
      setConfirmModal({
        isOpen: true, type: 'error', title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Aktualisieren', variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const processLocalDeletion = async (memberEmail: string, reference: string) => {
    setConfirmModal({
      isOpen: true, type: 'warning', title: 'Löschung bestätigen',
      message: `WARNUNG: Dies wird alle Daten von ${memberEmail} unwiderruflich löschen! Sind Sie sicher?`,
      variant: 'two',
      onConfirm: async () => {
        setLoading(true);
        try {
          const response = await apiClient.post('/api/privacy/process-deletion', { member_email: memberEmail, reference });
          if (response.data.success) {
            await apiClient.post(`/api/privacy/requests/${reference}/confirm-deletion`, {
              confirmation_text: 'Alle Daten wurden gemäß DSGVO Art. 17 gelöscht',
              deleted_data_summary: JSON.stringify(response.data.deletion_summary),
              member_email: memberEmail
            });
            setConfirmModal({
              isOpen: true, type: 'success', title: 'Erfolgreich',
              message: 'Löschung wurde erfolgreich durchgeführt und bestätigt', variant: 'single'
            });
            loadRequests();
          }
        } catch (err: any) {
          setConfirmModal({
            isOpen: true, type: 'error', title: 'Fehler',
            message: err.response?.data?.message || 'Fehler bei der Löschung', variant: 'single'
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const loadMemberData = async () => {
    if (!memberDataEmail) {
      setConfirmModal({ isOpen: true, type: 'error', title: 'Fehler', message: 'Bitte E-Mail-Adresse eingeben', variant: 'single' });
      return;
    }
    setLoading(true);
    setMemberData(null);
    try {
      const response = await apiClient.get(`/api/privacy/member-data/${memberDataEmail}`);
      setMemberData(response.data.data);
      setConfirmModal({ isOpen: true, type: 'success', title: 'Erfolgreich', message: 'Daten wurden erfolgreich geladen', variant: 'single' });
    } catch (err: any) {
      setConfirmModal({
        isOpen: true, type: 'error', title: 'Fehler',
        message: err.response?.data?.message || 'Fehler beim Laden der Mitgliedsdaten', variant: 'single'
      });
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = () => {
    if (!memberData || !memberData.personal_data) return;
    const doc = new jsPDF();
    let yPosition = 20;
    doc.setFontSize(20);
    doc.text('Dateneinsicht gemäß DSGVO Art. 15', 20, yPosition);
    yPosition += 10;
    doc.setFontSize(10);
    doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')} um ${new Date().toLocaleTimeString('de-DE')}`, 20, yPosition);
    yPosition += 10;
    doc.text(`Mitglied: ${memberDataEmail}`, 20, yPosition);
    yPosition += 15;

    if (memberData.personal_data) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('1. Persönliche Daten', 20, yPosition);
      doc.setFont('helvetica', 'normal');
      yPosition += 10;

      const personalData = memberData.personal_data;
      const personalInfo = [
        ['Name', `${personalData.vorname || ''} ${personalData.name || ''}`],
        ['Mitgliedsnummer', personalData.mitglied_nr || '-'],
        ['E-Mail', personalData.email || '-'],
        ['Telefon', personalData.tel_nummer || '-'],
        ['Adresse', `${personalData.strasse || ''}, ${personalData.plz || ''} ${personalData.ort || ''}`],
        ['Geburtsdatum', personalData.geb_am || '-'],
        ['Eintritt', personalData.eintritt || '-'],
        ['Status', personalData.active ? 'Aktiv' : 'Inaktiv']
      ];

      (doc as any).autoTable({ startY: yPosition, head: [['Feld', 'Wert']], body: personalInfo, theme: 'grid', styles: { fontSize: 9 } });
    }

    doc.save(`dateneinsicht_${memberDataEmail}_${Date.now()}.pdf`);
    setConfirmModal({ isOpen: true, type: 'success', title: 'Erfolgreich', message: 'PDF wurde erfolgreich heruntergeladen', variant: 'single' });
  };

  const exportToJSON = () => {
    if (!memberData) return;
    const dataStr = JSON.stringify(memberData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `dateneinsicht_${memberDataEmail}_${Date.now()}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    setConfirmModal({ isOpen: true, type: 'success', title: 'Erfolgreich', message: 'JSON wurde erfolgreich heruntergeladen', variant: 'single' });
  };

  useEffect(() => {
    if (activeTab === 'requests') loadRequests();
    else if (activeTab === 'statistics') loadStatistics();
  }, [activeTab]);

  // Status Badge
  const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const badges: any = {
      pending: { bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', text: 'text-amber-700 dark:text-amber-300', icon: <ClockIcon className="h-3 w-3" />, label: 'Ausstehend' },
      processing: { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)', text: 'text-blue-700 dark:text-blue-300', icon: <ArrowPathIcon className="h-3 w-3 animate-spin" />, label: 'In Bearbeitung' },
      completed: { bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.30)', text: 'text-emerald-700 dark:text-emerald-300', icon: <CheckCircleIcon className="h-3 w-3" />, label: 'Abgeschlossen' },
      rejected: { bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.30)', text: 'text-rose-700 dark:text-rose-300', icon: <XCircleIcon className="h-3 w-3" />, label: 'Abgelehnt' }
    };
    const b = badges[status] || badges.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-full ${b.text}`} style={{ background: b.bg, border: `1px solid ${b.border}` }}>
        {b.icon} {b.label}
      </span>
    );
  };

  const TypeBadge: React.FC<{ type: string }> = ({ type }) => {
    const types: any = {
      'data-access': { bg: 'rgba(59,130,246,0.10)', border: 'rgba(59,130,246,0.30)', text: 'text-blue-700 dark:text-blue-300', icon: <EyeIcon className="h-3 w-3" />, label: 'Dateneinsicht' },
      'delete-account': { bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.30)', text: 'text-rose-700 dark:text-rose-300', icon: <TrashIcon className="h-3 w-3" />, label: 'Löschung' }
    };
    const b = types[type] || types['data-access'];
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-full ${b.text}`} style={{ background: b.bg, border: `1px solid ${b.border}` }}>
        {b.icon} {b.label}
      </span>
    );
  };

  const tabBtn = (active: boolean) =>
    `flex-1 px-4 py-2.5 rounded-xl text-sm font-bold transition ${active ? 'bg-amber-500 text-slate-950' : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'}`;

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
          <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
            <div className="lg:col-span-8">
              <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
                ADMIN · DSGVO
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
                DSGVO-<br /><span className="text-amber-600 dark:text-amber-400">Center.</span>
              </h1>
              <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
                {statistics ? (
                  <>
                    <span className="font-bold text-gray-900 dark:text-white">{statistics.total_count} Anfragen</span> ·{' '}
                    <span className="font-bold text-amber-700 dark:text-amber-300">{statistics.pending_count} ausstehend</span> ·{' '}
                    <span className="font-bold text-emerald-700 dark:text-emerald-300">{statistics.completed_count} erledigt</span>.
                  </>
                ) : (
                  'Datenschutz-Anfragen, Statistiken und Mitglieder-Dateneinsicht — alles DSGVO-konform an einem Ort.'
                )}
              </p>
            </div>
            {statistics && (
              <div className="lg:col-span-4 grid grid-cols-3 gap-3">
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Gesamt</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-gray-900 dark:text-white">{statistics.total_count}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">Anfragen</div>
                </div>
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Pending</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-amber-600 dark:text-amber-400">{statistics.pending_count}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">offen</div>
                </div>
                <div className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">Erledigt</div>
                  <div className="text-2xl lg:text-3xl font-black mt-1 tabular-nums text-emerald-700 dark:text-emerald-300">{statistics.completed_count}</div>
                  <div className="text-[10px] text-gray-600 dark:text-slate-400 mt-0.5">fertig</div>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="px-4 sm:px-6 space-y-6">
          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-2xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/10">
            <button onClick={() => setActiveTab('requests')} className={tabBtn(activeTab === 'requests')}>Anfragen</button>
            <button onClick={() => setActiveTab('statistics')} className={tabBtn(activeTab === 'statistics')}>Statistiken</button>
            <button onClick={() => setActiveTab('data-export')} className={tabBtn(activeTab === 'data-export')}>Dateneinsicht</button>
          </div>

          {/* Statistics Tab Stats */}
          {activeTab === 'statistics' && statistics && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: 'Gesamt', value: statistics.total_count, color: 'text-gray-900 dark:text-white' },
                { label: 'Ausstehend', value: statistics.pending_count, color: 'text-amber-600 dark:text-amber-400' },
                { label: 'In Bearbeitung', value: statistics.processing_count, color: 'text-blue-700 dark:text-blue-300' },
                { label: 'Abgeschlossen', value: statistics.completed_count, color: 'text-emerald-700 dark:text-emerald-300' },
                { label: 'Dateneinsicht', value: statistics.data_access_count, color: 'text-blue-700 dark:text-blue-300' },
                { label: 'Löschungen', value: statistics.deletion_count, color: 'text-rose-700 dark:text-rose-300' },
              ].map((s) => (
                <div key={s.label} className={`rounded-2xl p-3 lg:p-4 ${cardClass}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500">{s.label}</div>
                  <div className={`text-2xl lg:text-3xl font-black mt-1 tabular-nums ${s.color}`}>{s.value || 0}</div>
                </div>
              ))}
            </div>
          )}

          {/* Tab Content */}
          <div className={`rounded-3xl p-6 ${cardClass}`}>
            {/* Tab: Anfragen */}
            {activeTab === 'requests' && (
              <div>
                <div className="mb-4">
                  <button onClick={loadRequests} className={`${primaryBtn} px-4 py-2.5 flex items-center gap-2`}>
                    <ArrowPathIcon className="h-5 w-5" />
                    Aktualisieren
                  </button>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-amber-500 border-t-transparent" />
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12">
                    <ShieldCheckIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-slate-400">Keine offenen Privacy-Anfragen vorhanden</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests.map((request) => (
                      <div key={request.reference_number} className={`rounded-2xl p-4 ${subTileClass}`}>
                        <div className="flex justify-between items-start mb-4 flex-wrap gap-2">
                          <div>
                            <h4 className="font-black text-gray-900 dark:text-white tracking-tight">{request.reference_number}</h4>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              <TypeBadge type={request.request_type} />
                              <StatusBadge status={request.status} />
                            </div>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-slate-400 tabular-nums">{new Date(request.created_at).toLocaleDateString('de-DE')}</div>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => loadRequestDetails(request.reference_number)}
                            className="px-3 py-1.5 bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-800 dark:text-slate-200 hover:bg-gray-200 dark:hover:bg-white/10 rounded-lg transition text-sm font-semibold"
                          >
                            Details
                          </button>
                          {request.status === 'pending' && (
                            <button
                              onClick={() => updateRequestStatus(request.reference_number, 'processing', 'Bearbeitung gestartet')}
                              className="px-3 py-1.5 bg-blue-500/15 border border-blue-500/30 text-blue-700 dark:text-blue-300 hover:bg-blue-500/25 rounded-lg transition text-sm font-bold"
                            >
                              Bearbeiten
                            </button>
                          )}
                          {request.status === 'processing' && request.request_type === 'delete-account' && (
                            <button
                              onClick={() => {
                                const email = prompt('E-Mail-Adresse des Mitglieds:');
                                if (email) processLocalDeletion(email, request.reference_number);
                              }}
                              className="px-3 py-1.5 bg-rose-500/15 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25 rounded-lg transition text-sm font-bold"
                            >
                              Löschung durchführen
                            </button>
                          )}
                        </div>

                        {selectedRequest?.reference_number === request.reference_number && (
                          <div className="mt-4 p-4 rounded-xl bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-700">
                            <h5 className="font-bold text-gray-900 dark:text-white mb-2 text-sm">Details:</h5>
                            <pre className="text-xs text-gray-700 dark:text-slate-300 overflow-auto">
                              {JSON.stringify(selectedRequest, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Tab: Statistiken */}
            {activeTab === 'statistics' && (
              <div className="text-center py-12">
                <ChartBarIcon className="h-12 w-12 text-gray-500 dark:text-slate-500 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-slate-400">Statistiken werden oben in den Karten angezeigt</p>
              </div>
            )}

            {/* Tab: Dateneinsicht */}
            {activeTab === 'data-export' && (
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-black text-gray-900 dark:text-white tracking-tight mb-4">Mitgliedsdaten für Dateneinsicht abrufen</h3>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <div className="flex-1">
                      <label className="block text-[10px] uppercase tracking-widest text-gray-500 dark:text-slate-500 font-bold mb-1">
                        <UserIcon className="inline h-3.5 w-3.5 mr-1" />
                        E-Mail-Adresse des Mitglieds
                      </label>
                      <input
                        type="email"
                        value={memberDataEmail}
                        onChange={(e) => setMemberDataEmail(e.target.value)}
                        placeholder="mitglied@example.com"
                        className={`w-full px-3 py-2.5 rounded-xl text-sm font-bold text-gray-900 dark:text-white outline-none ${inputClass}`}
                      />
                    </div>
                    <div className="flex sm:items-end">
                      <button
                        onClick={loadMemberData}
                        disabled={loading || !memberDataEmail}
                        className={`${primaryBtn} w-full sm:w-auto px-4 py-2.5 flex items-center justify-center gap-2`}
                      >
                        <MagnifyingGlassIcon className="h-5 w-5" />
                        Daten laden
                      </button>
                    </div>
                  </div>
                </div>

                {memberData && (
                  <div className="space-y-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                      <h3 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">Dateneinsicht für: {memberDataEmail}</h3>
                      <div className="flex gap-2 flex-wrap">
                        <button onClick={exportToJSON} className="px-4 py-2 bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/25 rounded-xl transition flex items-center gap-2 text-sm font-bold">
                          <ArrowDownTrayIcon className="h-5 w-5" />
                          JSON Export
                        </button>
                        <button onClick={exportToPDF} className="px-4 py-2 bg-rose-500/15 border border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25 rounded-xl transition flex items-center gap-2 text-sm font-bold">
                          <DocumentArrowDownIcon className="h-5 w-5" />
                          PDF Export
                        </button>
                      </div>
                    </div>

                    {memberData.personal_data && (
                      <div className={`rounded-2xl p-4 ${subTileClass}`}>
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                          <UserIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          Persönliche Daten
                        </h4>
                        <div className="max-h-60 overflow-y-auto">
                          <pre className="text-xs text-gray-700 dark:text-slate-300 overflow-auto p-3 rounded-lg bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-800">
                            {JSON.stringify(memberData.personal_data, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        visible={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant || 'single'}
        showCloseButton={true}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
        } : undefined}
        buttons={confirmModal.variant === 'two' ? {
          cancel: { text: 'Abbrechen', onPress: () => setConfirmModal({ ...confirmModal, isOpen: false }) },
          confirm: {
            text: 'Bestätigen',
            onPress: () => {
              confirmModal.onConfirm?.();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
      />
    </div>
  );
};

export default PrivacyManagement;
